import { dayDiff, fromIso, openDatePicker } from "./date_picker";
import { csrf, applyResponse, updateEntry } from "./request";

const RECENTS = 6;

function editorParts(editor: HTMLElement) {
  return {
    input: editor.querySelector<HTMLInputElement>("[data-pl-na-input]")!,
    date: editor.querySelector<HTMLInputElement>("[data-pl-na-date]")!,
    label: editor.querySelector<HTMLElement>("[data-pl-na-due-label]")!,
  };
}

export function dueLabel(iso: string): string {
  const date = fromIso(iso);
  const days = dayDiff(iso);
  if (!date || days === null) return "Set due date";
  const short = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  if (days < 0) return `Overdue · ${short}`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  if (days <= 6) return `Due ${date.toLocaleDateString("en-GB", { weekday: "long" })}`;
  return `Due ${short}`;
}

function showDue(editor: HTMLElement, iso: string): void {
  const { label } = editorParts(editor);
  const days = dayDiff(iso);
  label.textContent = dueLabel(iso);
  const chip = editor.querySelector<HTMLElement>(".pl-na-due");
  chip?.classList.toggle("is-set", !!iso);
  chip?.classList.toggle("is-overdue", days !== null && days < 0);
  const clear = editor.querySelector<HTMLElement>("[data-pl-na-due-clear]");
  if (clear) clear.hidden = !iso;
}

function list(editor: HTMLElement, key: "recents" | "presets"): string[] {
  try {
    return JSON.parse(editor.dataset[key] || "[]") as string[];
  } catch {
    return [];
  }
}

async function rememberRecent(editor: HTMLElement, text: string): Promise<void> {
  if (!text || list(editor, "presets").some((p) => p.toLowerCase() === text.toLowerCase())) return;
  const items = [text, ...list(editor, "recents").filter((r) => r.toLowerCase() !== text.toLowerCase())].slice(0, RECENTS);
  try {
    await fetch(editor.dataset.recentsUrl!, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": csrf(), Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ config: { items } }),
    });
  } catch {
    // recents are a convenience: the next action itself is already saved
  }
}

async function commit(editor: HTMLElement): Promise<void> {
  const { input, date } = editorParts(editor);
  const text = input.value.trim();
  const due = date.value;
  if (text === (editor.dataset.saved || "") && due === (editor.dataset.savedDue || "")) return;
  const card = editor.closest<HTMLElement>("[data-pl-card]");
  if (!card) return;
  editor.dataset.saved = text;
  editor.dataset.savedDue = due;
  const ok = applyResponse(await updateEntry(card.dataset.plCard!, { next_action: text, next_action_due: due, drawer: "1" }));
  if (ok) void rememberRecent(editor, text);
}

function revert(editor: HTMLElement): void {
  const { input, date } = editorParts(editor);
  input.value = editor.dataset.saved || "";
  date.value = editor.dataset.savedDue || "";
  showDue(editor, date.value);
}

let bound = false;

export function initNextAction(): void {
  if (bound) return;
  bound = true;
  document.addEventListener("keydown", (event) => {
    const input = event.target as HTMLElement;
    const editor = input.closest?.<HTMLElement>("[data-pl-next-action]");
    if (!editor || !input.matches("[data-pl-na-input]")) return;
    if (event.key === "Enter") {
      event.preventDefault();
      (input as HTMLInputElement).blur();
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      revert(editor);
      (input as HTMLInputElement).blur();
    }
  });
  document.addEventListener("focusout", (event) => {
    const input = event.target as HTMLElement;
    const editor = input.closest?.<HTMLElement>("[data-pl-next-action]");
    if (editor && input.matches("[data-pl-na-input]")) void commit(editor);
  });
  document.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const editor = target.closest<HTMLElement>("[data-pl-next-action]");
    if (!editor) return;
    const chip = target.closest<HTMLElement>("[data-pl-na-chip]");
    const dueButton = target.closest<HTMLButtonElement>("[data-pl-na-due]");
    const { input, date } = editorParts(editor);
    if (dueButton) {
      openDatePicker(dueButton, date.value, (iso) => {
        date.value = iso;
        showDue(editor, iso);
        void commit(editor);
      });
    } else if (target.closest("[data-pl-na-due-clear]")) {
      date.value = "";
      showDue(editor, "");
      void commit(editor);
    } else if (chip) {
      input.value = chip.dataset.plNaChip || "";
      void commit(editor);
    } else if (target.closest("[data-pl-na-clear]")) {
      input.value = "";
      date.value = "";
      void commit(editor);
    }
  });
}
